import { HsvColorPicker } from 'react-colorful';

import { InContentModal } from '../../../../../../../helpers/modals/InContentModal';
import { InputField } from '../../../../../../../helpers/FormFields/InputField';
import { modelViewersKeys, words } from '../../../../../../../locales/keys';

import { useModelViewerColorPicker } from './hooks';

const inputClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-2 py-1 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500';

const labelClass =
  'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

interface ModelViewerColorPickerPopupProps {
  isOpen: boolean;
  initialColor: string;
  onChangePreview: (hex: string) => void;
  onConfirm: (hex: string) => void;
  onCancel: () => void;
}

function ModelViewerColorPickerPopup({
  isOpen,
  initialColor,
  onChangePreview,
  onConfirm,
  onCancel
}: ModelViewerColorPickerPopupProps) {
  const {
    hsv,
    format,
    hexInput,
    rgb,
    hsl,
    setFormat,
    handlePickerChange,
    handleHexInput,
    handleRgbInput,
    handleHslInput,
    handleConfirm,
    handleCancel
  } = useModelViewerColorPicker({
    isOpen,
    initialColor,
    onChangePreview,
    onConfirm,
    onCancel
  });

  return (
    <InContentModal
      isOpen={isOpen}
      hideModal={handleCancel}
      i18nTitle={modelViewersKeys.backgroundColorPicker}
      i18nSubmitText={words.confirm}
      modalSize="sm"
      onCancel={handleCancel}
      handleSubmit={handleConfirm}
    >
      <div className="p-4">
        <style>{`
          .mv-color-picker.react-colorful { width: 100%; height: 220px; }
          .mv-color-picker .react-colorful__saturation { border-radius: 8px 8px 0 0; border-bottom-width: 8px; }
          .mv-color-picker .react-colorful__hue { border-radius: 0 0 8px 8px; }
          .mv-color-picker .react-colorful__pointer { width: 28px; height: 28px; border: 2px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,.4); }
        `}</style>

        <HsvColorPicker
          color={hsv}
          onChange={handlePickerChange}
          className="mv-color-picker"
        />

        <div className="flex w-full gap-3 mt-4">
          <div className="w-20">
            <label className={labelClass}>Format</label>
            <select
              value={format}
              onChange={(e) =>
                setFormat(e.target.value as 'hex' | 'rgb' | 'hsl')
              }
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm px-2 py-1 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="hex">Hex</option>
              <option value="rgb">RGB</option>
              <option value="hsl">HSL</option>
            </select>
          </div>

          {format === 'hex' && (
            <InputField
              className="flex-1"
              id="mv-color-hex"
              name="mv-color-hex"
              type="text"
              value={hexInput}
              onChange={handleHexInput}
              inputClassName={inputClass}
              label="Hex value"
              labelClassName={labelClass}
              showError={false}
            />
          )}

          {format === 'rgb' && (
            <>
              {(['r', 'g', 'b'] as const).map((ch) => (
                <InputField
                  key={ch}
                  className="flex-1"
                  id={`mv-color-rgb-${ch}`}
                  name={`mv-color-rgb-${ch}`}
                  type="number"
                  min={0}
                  max={255}
                  value={String(rgb[ch])}
                  onChange={(e) => handleRgbInput(ch, e.target.value)}
                  inputClassName={inputClass}
                  label={ch.toUpperCase()}
                  labelClassName={labelClass}
                  showError={false}
                />
              ))}
            </>
          )}

          {format === 'hsl' && (
            <>
              {(
                [
                  { ch: 'h', max: 360, label: 'H' },
                  { ch: 's', max: 100, label: 'S' },
                  { ch: 'l', max: 100, label: 'L' }
                ] as const
              ).map(({ ch, label, max }) => (
                <InputField
                  key={ch}
                  className="flex-1"
                  id={`mv-color-hsl-${ch}`}
                  name={`mv-color-hsl-${ch}`}
                  type="number"
                  min={0}
                  max={max}
                  value={String(hsl[ch])}
                  onChange={(e) => handleHslInput(ch, e.target.value)}
                  inputClassName={inputClass}
                  label={label}
                  labelClassName={labelClass}
                  showError={false}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </InContentModal>
  );
}

export default ModelViewerColorPickerPopup;
